import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Downloads.css';

const AiLauncher = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const videoId = searchParams.get('video_id');
  const highlightId = searchParams.get('highlight');
  const videoSectionId = searchParams.get('video');
  const categoryParam = searchParams.get('category');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [resources, setResources] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedResources, setGroupedResources] = useState({});

  useEffect(() => {
    if (videoId) {
      fetchData();
    } else {
      fetchCategories();
    }
  }, [videoId]);

  useEffect(() => {
    if (categoryParam && selectedCategory !== categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [categoryParam]);

  useEffect(() => {
    if (videoSectionId && !videoId && !categoryParam) {
      axios.get(`/api/ai-launcher?video_id=${videoSectionId}`)
        .then((res) => {
          const list = res.data || [];
          const cat = list[0]?.category;
          if (cat) setSelectedCategory(cat);
        })
        .catch(() => {});
    }
  }, [videoSectionId, videoId, categoryParam]);

  useEffect(() => {
    if (selectedCategory !== null && !videoId) {
      fetchData();
    }
  }, [selectedCategory, videoId]);

  useEffect(() => {
    if (highlightId && selectedCategory === null && !videoId) {
      axios.get(`/api/ai-launcher/${highlightId}`)
        .then((res) => {
          const cat = res.data?.category;
          if (cat) setSelectedCategory(cat);
        })
        .catch(() => {});
    }
  }, [highlightId, videoId]);

  useEffect(() => {
    if (videoSectionId && Object.keys(groupedResources).length > 0) {
      const el = document.getElementById(`video-section-${videoSectionId}`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
        el.classList.add('highlight-flash');
        const t = setTimeout(() => el.classList.remove('highlight-flash'), 2000);
        return () => clearTimeout(t);
      }
    }
  }, [videoSectionId, groupedResources]);

  useEffect(() => {
    if (highlightId && resources.length > 0) {
      const el = document.getElementById(`ai-launcher-resource-${highlightId}`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
        el.classList.add('highlight-flash');
        const t = setTimeout(() => el.classList.remove('highlight-flash'), 2000);
        return () => clearTimeout(t);
      }
    }
  }, [highlightId, resources]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/categories?table=ai_launcher_resources');
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('获取分类列表失败:', error);
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      let url = '/api/ai-launcher';
      if (videoId) {
        url = `/api/ai-launcher?video_id=${videoId}`;
      } else if (selectedCategory) {
        url = `/api/ai-launcher?category=${encodeURIComponent(selectedCategory)}`;
      }

      const response = await axios.get(url);
      const resourcesData = response.data;
      setResources(resourcesData);

      if (!videoId) {
        const videosResponse = await axios.get('/api/videos');
        setVideos(videosResponse.data);

        const grouped = {};
        resourcesData.forEach((resource) => {
          const relatedVideoIds = (resource.video_ids && resource.video_ids.length > 0)
            ? resource.video_ids
            : (resource.video_id ? [resource.video_id] : ['unassigned']);

          relatedVideoIds.forEach((vid) => {
            const key = vid || 'unassigned';
            if (!grouped[key]) {
              grouped[key] = [];
            }
            grouped[key].push(resource);
          });
        });
        setGroupedResources(grouped);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setResources([]);
    setGroupedResources({});
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      '百度网盘': '📦',
      '夸克网盘': '☁️',
      '阿里云盘': '💾',
      '迅雷云盘': '⚡',
      '123云盘': '☁️',
      '直链地址': '🌐',
      'ModelScope': '🤖',
      '魔搭社区': '🤖',
      '抱抱脸': '🤗',
      'C站': '🎨',
      '其他': '🔗'
    };
    return icons[platform] || '🔗';
  };

  const getPlatformColor = (platform) => {
    const colors = {
      '百度网盘': '#1890ff',
      '夸克网盘': '#ff6b00',
      '阿里云盘': '#1890ff',
      '迅雷云盘': '#ff6b00',
      '123云盘': '#00a8ff',
      '直链地址': '#667eea',
      'ModelScope': '#764ba2',
      '魔搭社区': '#764ba2',
      '抱抱脸': '#ffd700',
      'C站': '#6366f1',
      '其他': '#999'
    };
    return colors[platform] || '#999';
  };

  const renderResourceCard = (resource) => (
    <div key={resource.id} id={`ai-launcher-resource-${resource.id}`} className="download-card">
      <div className="download-header">
        <div className="platform-badge" style={{ backgroundColor: getPlatformColor(resource.platform) }}>
          <span className="platform-icon">{getPlatformIcon(resource.platform)}</span>
          <span className="platform-name">{resource.platform}</span>
        </div>
      </div>
      <div className="download-content">
        <h3 className="download-title">{resource.title}</h3>
        {resource.description && (
          <p className="download-description">{resource.description}</p>
        )}
        <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
          <a
            href={resource.url}
            target="_blank"
            rel="noopener noreferrer"
            className="download-btn"
          >
            {resource.platform}下载
          </a>
          {resource.official_url && (
            <a
              href={resource.official_url}
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn"
              style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              官方下载
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="downloads-container fade-in">
      <div className="page-header">
        <h1 className="page-title">AI创作助手</h1>
        <p className="page-subtitle">
          {videoId
            ? '该视频的相关AI创作助手资源'
            : selectedCategory
              ? `${selectedCategory} - AI创作助手资源`
              : '官方AI客户端、运行环境、模型与相关资源下载'}
        </p>
        {videoId && (
          <button
            onClick={() => navigate('/ai-launcher')}
            className="back-btn"
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', cursor: 'pointer' }}
          >
            ← 查看所有资源
          </button>
        )}
        {selectedCategory && !videoId && (
          <button
            onClick={handleBackToCategories}
            className="back-btn"
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', cursor: 'pointer' }}
          >
            ← 返回分类列表
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">加载中...</div>
      ) : videoId ? (
        resources.length === 0 ? (
          <div className="empty-state">
            <p>该视频暂无相关AI创作助手资源</p>
          </div>
        ) : (
          <div className="downloads-grid">
            {resources.map(renderResourceCard)}
          </div>
        )
      ) : selectedCategory === null ? (
        categories.length === 0 ? (
          <div className="empty-state">
            <p>暂无分类</p>
          </div>
        ) : (
          <div className="categories-grid">
            {categories.map((category, index) => (
              <div
                key={index}
                className="category-card"
                onClick={() => handleCategoryClick(category)}
              >
                <h3 className="category-title">{category}</h3>
                <p className="category-description">点击查看该分类下的AI创作助手资源</p>
              </div>
            ))}
          </div>
        )
      ) : (
        Object.keys(groupedResources).length === 0 ? (
          <div className="empty-state">
            <p>该分类下暂无AI创作助手资源</p>
          </div>
        ) : (
          <div className="downloads-by-video">
            {Object.keys(groupedResources).map((vid) => {
              const videoResources = groupedResources[vid];
              const video = vid !== 'unassigned' ? videos.find(v => v.id === parseInt(vid)) : null;

              return (
                <div key={vid} id={`video-section-${vid}`} className="video-download-section">
                  <h2 className="video-section-title">
                    {video ? video.title : '通用资源'}
                    {video && (
                      <a
                        href={`https://www.bilibili.com/video/${video.bvid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-video-link"
                      >
                        查看视频 →
                      </a>
                    )}
                  </h2>
                  <div className="downloads-grid">
                    {videoResources.map(renderResourceCard)}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default AiLauncher;
